import type { Provider } from "./types";

/**
 * Registry for managing email providers
 */
class ProviderRegistry {
  private providers: Map<string, Provider>;

  constructor() {
    this.providers = new Map();
  }

  /**
   * Register a new provider
   * @param provider The provider to register
   * @throws Error if provider with same ID already exists
   */
  register(provider: Provider): void {
    if (this.providers.has(provider.id)) {
      throw new Error(`Provider with id ${provider.id} already registered`);
    }
    this.providers.set(provider.id, provider);
  }

  /**
   * Get a provider by ID
   * @param id The provider ID
   * @returns The provider or undefined if not found
   */
  getProvider(id: string): Provider | undefined {
    return this.providers.get(id);
  }

  /**
   * Get all registered providers
   * @returns Array of registered providers
   */
  getAllProviders(): Provider[] {
    return Array.from(this.providers.values());
  }

  /**
   * Check if a provider is registered
   * @param id The provider ID
   * @returns true if provider is registered
   */
  hasProvider(id: string): boolean {
    return this.providers.has(id);
  }
}

// Create and export singleton instance
export const providerRegistry = new ProviderRegistry();

// Export class for testing purposes
export { ProviderRegistry };
